import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
// @ts-ignore
import ChatButton from "../components/chat-button/chat-button.tsx";
// @ts-ignore
import ChatHeader from "../components/chat-header/chat-header.tsx";
// @ts-ignore
import ChatBody from "../components/chat-body/chat-body.tsx";
// @ts-ignore
import ChatFooter from "../components/chat-footer/chat-footer.tsx";
// @ts-ignore
import { useWebSocketManager } from "../components/web-socket-manager/web-socket-manager.ts";
// @ts-ignore
import ConciergePopup from "../components/ConciergePopup.tsx";
// @ts-ignore
import { AuthModal } from "../components/auth-modal/auth-modal.tsx";
// @ts-ignore
import { ProviderModal } from "../components/provider-modal/provider-modal.tsx";
// @ts-ignore
import { useMemo } from "react";

export interface ChatWindowProps {
  topic_name?: string;
}

export interface ChatWindowRef {
  toggle_chat: () => void;
  get_config: () => IConfig | null;
}

export interface IConfig {
  topic_name: string;
  bg_image: string;
  brand_logo: string;
  brand_name: string;
  toggle_bg_color: string; // Chat btn
  toggle_text_color: string; // Chat btn
  brand_primary_bg_color: string;
  brand_primary_text_color: string;
  brand_secondary_bg_color: string;
  brand_secondary_text_color: string;
  brand_tertiary_bg_color: string;
  brand_tertiary_text_color: string;
  concierge_name?: string;
  show_provider_modal?: boolean; // Controls whether the provider modal is shown
  pop_up_list?: string[];
  sms_phone_number?: string;
  sms_message?: string;
}

const ChatWindow = forwardRef<ChatWindowRef, ChatWindowProps>((props, ref) => {
  const [input_message, set_input_message] = useState("");
  const [show_chat, set_show_chat] = useState(false);
  const [prev_messages, set_prev_messages] = useState<any>([]);
  const [show_pop_up, set_show_pop_up] = useState<any>(false);
  const messages_end_ref = useRef<HTMLDivElement>(null);
  const is_mobile = useRef(window.innerWidth <= 600);
  const [new_url, set_new_url] = useState("");

  const [show_auth_modal, set_show_auth_modal] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [auth_error, set_auth_error] = useState("");
  const [is_authenticating, set_is_authenticating] = useState(false);

  // State for provider modal
  const [show_provider_modal, set_show_provider_modal] = useState(false);
  const [provider_selection_complete, set_provider_selection_complete] =
    useState(false);

  const pathName = useMemo(() => {
    return window.location.pathname;
  }, []);

  // Default values if path does not match
  const config = useMemo(() => {
    if (pathName && topic_config[pathName]) return topic_config[pathName];
    return null; // Return null instead of default config
  }, [pathName]);

  // Initialize hook values that depend on config - with a fallback object if config is null
  // Using useWebSocketManager conditionally but in a way that satisfies React Hooks rules
  let topic_name = config?.topic_name || "brekiya";

  if (config?.topic_name === "coopervision") {
    topic_name = "misight";
  }

  const {
    messages,
    loading,
    is_reconnecting,
    send_message,
    create_websocket_connection,
    close_connection,
  } = useWebSocketManager(topic_name);

  function onUrlChange(newUrl: string) {
    set_new_url(newUrl);
  }

  // Handle window resize
  useEffect(() => {
    // Check if we need to show the provider modal first
    if (config) {
      if (config.show_provider_modal && !provider_selection_complete) {
        set_show_provider_modal(true);
      } else {
        set_show_pop_up(true);
      }
    }

    const handle_resize = () => {
      is_mobile.current = window.innerWidth <= 600;
    };

    window.addEventListener("resize", handle_resize);
    handle_resize(); // Initialize on mount

    window.addEventListener("popstate", function () {
      onUrlChange(window.location.href);
    });

    // Listen for hashchange event (for hash-based routing)
    window.addEventListener("hashchange", () => {
      onUrlChange(window.location.href);
    });

    // Intercept history methods
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function () {
      originalPushState.apply(this, arguments);
      onUrlChange(window.location.href);
    };

    window.history.replaceState = function () {
      originalReplaceState.apply(this, arguments);
      onUrlChange(window.location.href);
    };

    return () => {
      window.removeEventListener("resize", handle_resize);
    };
  }, [config]); // Added config as a dependency

  // Handler for provider selection
  const handleProviderSelect = () => {
    set_provider_selection_complete(true);
    set_show_provider_modal(false);
    // Show popup after provider selection
    set_show_pop_up(true);
  };

  // Handler for patient selection
  const handlePatientSelect = () => {
    set_provider_selection_complete(true);
    set_show_provider_modal(false);
    // Show popup after patient selection
    set_show_pop_up(true);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (show_chat) {
      if (loading || messages?.length !== prev_messages?.length) {
        scroll_to_bottom();
      }
    }
    if (messages) {
      set_prev_messages(messages);
    }
  }, [messages, show_chat, loading, prev_messages]);

  // Handle page unload
  useEffect(() => {
    const handle_page_unload = () => {
      sessionStorage.removeItem("conversation_id");
    };

    window.addEventListener("beforeunload", handle_page_unload);

    return () => {
      window.removeEventListener("beforeunload", handle_page_unload);
    };
  }, []);

  // Toggle chat window and handle WebSocket connection
  const toggle_chat = () => {
    if (!show_chat) {
      // Only create WebSocket connection when opening chat
      set_show_chat(true);
      create_websocket_connection();
    } else {
      set_show_chat(false);
    }
  };

  // Expose methods to parent component via ref - must be before early return
  useImperativeHandle(ref, () => ({
    toggle_chat,
    get_config: () => config,
  }));

  // Add early return if path is not valid - must be AFTER all hooks
  if (!config) {
    return (
      <div>
        <div>
          <h2>Access Denied</h2>
          <p>This chat service is not available at this URL.</p>
        </div>
      </div>
    );
  }

  const scroll_to_bottom = () => {
    if (messages_end_ref.current) {
      messages_end_ref.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  const handle_send_message = () => {
    if (send_message(input_message)) {
      set_input_message("");
    }
  };

  // Handle button click
  const handle_button_click = (button_text: string) => {
    send_message(button_text);
  };

  // Handle close button click
  const handle_close_chat = () => {
    set_show_chat(false);
    // close_connection();
  };

  // @ts-ignore
  const handleAuthenticate = async (e: any): Promise<void> => {
    e.preventDefault();
    set_is_authenticating(true);
    set_auth_error("");

    // Simulate network request
    // @ts-ignore
    await new Promise((resolve) => setTimeout(resolve, 1000));

    if (username === "user" && password === "password") {
      set_show_auth_modal(false);
    } else {
      set_auth_error("Invalid username or password");
    }

    set_is_authenticating(false);
  };

  return (
    <div
      className="chat-component"
      style={{
        background: `url(${config.bg_image})`,
        backgroundSize: "cover",
        width: "100%",
        height: "100vh",
        zIndex: show_chat ? 100 : show_pop_up ? 100 : 8,
      }}
    >
      {/* Chat button fixed at the bottom right */}
      <ChatButton
        config={config}
        toggle_chat={toggle_chat}
        show_chat={show_chat}
        new_url={new_url}
      />

      {/* Provider Modal - shown when show_provider_modal is true */}
      {config?.show_provider_modal &&
        show_provider_modal &&
        !provider_selection_complete && (
          <ProviderModal
            config={config}
            onClose={() => {
              set_show_provider_modal(false);
              set_provider_selection_complete(true);
              set_show_pop_up(true);
            }}
            onProviderSelect={handleProviderSelect}
            onPatientSelect={handlePatientSelect}
          />
        )}

      {/* Concierge Popup - only shown if provider modal is not shown or has been completed */}
      {!show_chat &&
      show_pop_up &&
      (!config?.show_provider_modal || provider_selection_complete) ? (
        <ConciergePopup
          config={config}
          onMessageClicked={(mssg = "") => {
            toggle_chat();
            set_show_pop_up(false);
          }}
          onOpenChat={() => {
            toggle_chat();
            set_show_pop_up(false);
          }}
        />
      ) : null}
      {/* Authentication Modal */}
      {/*{show_auth_modal && (*/}
      {/*	<AuthModal*/}
      {/*		handleAuthenticate={handleAuthenticate}*/}
      {/*		username={username}*/}
      {/*		setUsername={setUsername}*/}
      {/*		password={password}*/}
      {/*		setPassword={setPassword}*/}
      {/*		authError={auth_error}*/}
      {/*		isAuthenticating={is_authenticating}*/}
      {/*	/>*/}
      {/*)}*/}

      {/* Chat window that shows only when activated */}
      {show_chat && (
        <div>
          <div className="backdrop" />
          <div className="chat-wrapper">
            <div className="chat-window">
              <ChatHeader onClose={handle_close_chat} config={config} />
              <ChatBody
                messages={messages}
                config={config}
                loading={loading}
                is_reconnecting={is_reconnecting}
                messages_end_ref={messages_end_ref}
                handle_button_click={handle_button_click}
              />
              <div ref={messages_end_ref} />
              <ChatFooter
                input_message={input_message}
                set_input_message={set_input_message}
                send_message={handle_send_message}
                is_reconnecting={is_reconnecting}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ChatWindow.displayName = "ChatWindow";

export default ChatWindow;

const topic_config: Record<string, IConfig> = {
  "/brekiya": {
    topic_name: "brekiya",
    bg_image: "/brekiya-bg.png",
    brand_logo: "/brekiya_logo.webp",
    brand_name: "Brekiya",
    toggle_bg_color: "#2C3E50",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#0066FF",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#009B49",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#0066FF",
    brand_tertiary_text_color: "#FFFFFF",
  },
  "/veozah": {
    topic_name: "veozah",
    bg_image: "/veozah_bg.png",
    brand_logo: "/veozah.svg",
    brand_name: "Vezah",
    toggle_bg_color: "#DAC961",
    toggle_text_color: "#241756",
    brand_primary_bg_color: "#8E2067",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#DAC961",
    brand_secondary_text_color: "#241756",
    brand_tertiary_bg_color: "#361659",
    brand_tertiary_text_color: "#FFFFFF",
  },
  "/ohtuvayre": {
    topic_name: "ohtuvayre",
    bg_image: "/ohtuvayre_bg.png",
    brand_logo: "/ohtuvayre-logo.png",
    brand_name: "Ohtuvayre",
    toggle_bg_color: "#7A38CF",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#362F73",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#7A38CF",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#83D9F5",
    brand_tertiary_text_color: "#000000",
  },
  "/stiolto": {
    topic_name: "stiolto",
    bg_image: "/stiolto-bg.png",
    brand_logo: "/stiolto_logo.png",
    brand_name: "Stiolto",
    toggle_bg_color: "#76BC43",
    toggle_text_color: "#f1f1f3",
    brand_primary_bg_color: "#2B7F85",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#76BC43",
    brand_secondary_text_color: "#f1f1f3",
    brand_tertiary_bg_color: "#3E6310",
    brand_tertiary_text_color: "#FFFFFF",
  },
  "/crexont": {
    topic_name: "crexont",
    bg_image: "/crexont_bg.png",
    brand_logo: "/crexont_logo.svg",
    brand_name: "CREXONT",
    toggle_bg_color: "#2C3E50",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#2C3E50",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#DEFAB2",
    brand_secondary_text_color: "#2C3E50",
    brand_tertiary_bg_color: "#76BC43",
    brand_tertiary_text_color: "#2C3E50",
  },
  "/unithroid": {
    topic_name: "unithroid",
    bg_image: "/unithroid_bg.png",
    brand_logo: "/unithroid_logo.svg",
    brand_name: "Unithroid",
    toggle_bg_color: "#5281C9",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#245187",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#5281C9",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#245187",
    brand_tertiary_text_color: "#FFFFFF",
    pop_up_list: [
      "Find a Pharmacy",
      "Learn about Unithroid",
      "Get Cost Support",
      "Talk to a Live Concierge",
    ],
  },
  "/dupixent": {
    topic_name: "dupixent",
    bg_image: "/dupixent_bg.png",
    brand_logo: "/dupixent_logo.svg",
    brand_name: "Dupixent",
    toggle_bg_color: "#096",
    toggle_text_color: "#fff",
    brand_primary_bg_color: "#017a60",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#99CC00",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#E27100",
    brand_tertiary_text_color: "#FFFFFF",
  },
  "/misight": {
    topic_name: "misight",
    bg_image: "/misight_bg.png",
    brand_logo: "/misight_logo.png",
    brand_name: "Misight",
    toggle_bg_color: "#CF027F",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#CF027F",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#146FF8",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#6C2196",
    brand_tertiary_text_color: "#FFFFFF",
  },
  "/coopervision": {
    topic_name: "coopervision",
    bg_image: "/coopervision_bg.png",
    brand_logo: "/coopervision_logo.webp",
    brand_name: "Coopervision",
    toggle_bg_color: "#005cab",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#6D2097",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#2d9a43",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#005cab",
    brand_tertiary_text_color: "#FFFFFF",
  },
  "/pemgarda": {
    topic_name: "pemgarda",
    bg_image: "/pemgarda-bg.png",
    brand_logo: "/pemgarda-logo.png",
    brand_name: "Pemgarda",
    toggle_bg_color: "#294880",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#FA953B",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#294880",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#E24C1C",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Find/Schedule Doctor",
      "Find Infusion Center",
      "Learn About pemgarda",
      "Talk to Live Concierge",
      "Find a Pharmacy",
    ],
  },
  "/brenzavvy": {
    topic_name: "brenzavvy",
    bg_image: "/brenzavvy-bg.png",
    brand_logo: "/brenzavvy-logo.png",
    brand_name: "Brenzavvy",
    toggle_bg_color: "#FFFFFF",
    toggle_text_color: "#FD8C35",
    brand_primary_bg_color: "#328799",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#5FC9B6",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#FCA750",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Find/Schedule a Doctor Online",
      "Learn About brenzavvy",
      "Get Cost Support",
      "Talk to Live Concierge",
      "Find a Pharmacy",
    ],
  },
  "/rxassist": {
    topic_name: "dupixent",
    bg_image: "/rx-assist-bg.png",
    brand_logo: "/rx-assist-logo.gif",
    brand_name: "Rx Assist",
    toggle_bg_color: "#FFFFFF",
    toggle_text_color: "#3399CB",
    brand_primary_bg_color: "#328799",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#5FC9B6",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#FCA750",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: true,
  },
  "/jatenzo": {
    topic_name: "jatenzo",
    bg_image: "/jatenzo-bg.png",
    brand_logo: "/jatenzo-logo.svg",
    brand_name: "Jatenzo",
    toggle_bg_color: "#FF671F",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#58B947",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#FA651E",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#333",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Why JATENZO?",
      "Safe & Effective",
      "Help Paying for JATENZO",
      "FAQs & Resources",
      "Find a TRT Doctor",
    ],
  },
  "/wakix": {
    topic_name: "wakix",
    bg_image: "/wakix-bg.png",
    brand_logo: "/wakix-logo.webp",
    brand_name: "Wakix",
    toggle_bg_color: "#005DB9",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#00963B",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#005DB9",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#636569",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
  },
  "/symbravo-hcp": {
    topic_name: "symbravo-hcp",
    bg_image: "/symbravo_bg.png",
    brand_logo: "/symbravo.svg",
    brand_name: "Symbravo HCP",
    toggle_bg_color: "#E8CC79",
    toggle_text_color: "#303030",
    brand_primary_bg_color: "#FFE084",
    brand_primary_text_color: "#303030",
    brand_secondary_bg_color: "#31CAAB",
    brand_secondary_text_color: "#303030",
    brand_tertiary_bg_color: "#dadada",
    brand_tertiary_text_color: "#303030",
    pop_up_list: [
      "Learn More about Symbravo",
      "Request a Rep",
      "Request Samples",
      "Get Cost Support for Patients",
      "Talk to a Live Concierge",
    ],
    show_provider_modal: false,
  },
  "/tyrvaya": {
    topic_name: "Tyrvaya",
    bg_image: "/tyrvarya-bg.png",
    brand_logo: "/tyrvaya.png",
    brand_name: "Tyrvaya",
    toggle_bg_color: "#050F9E",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#050F9E",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#00CE7C",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#0099CC",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
  },
  "/voquezna-hcp": {
    topic_name: "voquezna-hcp",
    bg_image: "/voquezna-hcp.png",
    brand_logo: "voquezna.svg",
    brand_name: "Voquezna HCP",
    toggle_bg_color: "#0B437E",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#B22C58",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#67B6F8",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#dadada",
    brand_tertiary_text_color: "#303030",
    pop_up_list: [
      "Learn More about Voquenza",
      "Request a Rep",
      "Request Samples",
      "Get Cost Support for Patients",
      "Talk to a Live Concierge",
    ],
    show_provider_modal: false,
  },
  "/qlosi": {
    topic_name: "qlosi",
    bg_image: "/qlosi_bg.png",
    brand_logo: "/qlosi_logo.webp",
    brand_name: "Qlosi",
    toggle_bg_color: "#7fcbae",
    toggle_text_color: "#4B336F",
    brand_primary_bg_color: "#4B336F",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#DE6D4D",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#7FCBAE",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
  },
  "/austedo": {
    topic_name: "austedo",
    bg_image: "/austedo_bg.png",
    brand_logo: "/austedo_logo.svg",
    brand_name: "AUSTEDO XR",
    toggle_bg_color: "#6d2f74",
    toggle_text_color: "#ffffff",
    brand_primary_bg_color: "#6D2F74",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#00567A",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#F89626",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn more about AUSTEDO XR",
      "Access Teva Shared Services",
      "Get Cost Support",
      "Talk to a Live Concierge",
    ],
  },
  "/zepbound": {
    topic_name: "zepbound",
    bg_image: "/zepbound-bg.png",
    brand_logo: "/zepbound_logo.svg",
    brand_name: "Zepbound",
    toggle_bg_color: "#79DE00",
    toggle_text_color: "#15374E",
    brand_primary_bg_color: "#15374E",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#0593F0",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#79DE00",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Find/Schedule a Doctors Appt",
      "Learn more about Zepbound",
      "Get Cost Support",
      "Talk to a Live Agent",
    ],
  },
  "/nexlizet": {
    topic_name: "nexlizet",
    bg_image: "/nexlizet-bg.png",
    brand_logo: "/nexlizet-logo.svg",
    brand_name: "NEXLIZET",
    toggle_bg_color: "#AF1685",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#AF1685",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#E09601",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#007AC3",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn more about Nexlizet",
      "Find a Doctor",
      "Get Cost Support",
      "Talk to a Live Concierge",
    ],
  },
  "/nexlizet-hcp": {
    topic_name: "nexlizet-hcp",
    bg_image: "/nexlizet-hcp.png",
    brand_logo: "/nexlizet-logo.svg",
    brand_name: "NEXLIZET HCP",
    toggle_bg_color: "#AF1685",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#ED1C8F",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#007AC3",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#E09601",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn more about Nexlizet",
      "Get Cost Support for Patients",
      "Request a Rep or Samples",
      "Talk to a Live Concierge",
    ],
  },
  "/kerendia": {
    topic_name: "kerendia",
    bg_image: "/kerendia_bg.png",
    brand_logo: "/kerendia-logo.svg",
    brand_name: "KERENDIA",
    toggle_bg_color: "#B00000",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#CD0000",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#B64E5C",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#B64E5C",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about KERENDIA",
      "Get Cost Support",
      "How to Take KERNENDIA",
      "Talk to a Live Concierge",
    ],
  },
  "/ultomiris": {
    topic_name: "ultomiris",
    bg_image: "/ultomiris-bg.png",
    brand_logo: "/ultomiris-logo.png",
    brand_name: "ULTOMIRIS",
    toggle_bg_color: "#05665c",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#005F56",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#B84E4C",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#EE995C",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    concierge_name: "Iris",
    pop_up_list: [
      "Learn More about ULTOMIRIS",
      "Find an Infusion Center",
      "Get Support Resources",
      "Connect to Live Concierge",
    ],
  },
  "/opzelura": {
    topic_name: "opzelura",
    bg_image: "/opzelura-bg.png",
    brand_logo: "/opzelura.png",
    brand_name: "OPZELURA",
    toggle_bg_color: "#2D2850",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#2D2850",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#1E96A4",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#F57B7C",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about OPZELURA",
      "Book a Virtual Appt",
      "Request Financial Support",
      "Patient Support Options",
      "Connect with a Live Agent",
    ],
  },
  "/lupkynis": {
    topic_name: "lupkynis",
    bg_image: "/lupkynis-bg.png",
    brand_logo: "/lupkynis.png",
    brand_name: "LUPKYNIS",
    toggle_bg_color: "#023038",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#023038",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#086763",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#F86E59",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn more about Lupkynis",
      "Access Lupkynis Resources",
      "Find a Doctor",
      "Connect to a Live Concierge",
    ],
  },
  "/yupelri": {
    topic_name: "yupelri",
    bg_image: "/yupelri-bg.png",
    brand_logo: "/yupelri.png",
    brand_name: "YUPELRI",
    toggle_bg_color: "#005EB8",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#005EB8",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#765DB8",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#509E2F",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
  },
  "/mounjaro": {
    topic_name: "mounjaro",
    bg_image: "/mounjaro-bg.png",
    brand_logo: "/mounjaro.png",
    brand_name: "Mounjaro",
    toggle_bg_color: "#5D0096",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#5D0096",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#E43930",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#509E2F",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Getting Started",
      "Learn More about Mounjaro",
      "Resources for Saving",
      "Dosing Options",
      "Talk to a Live Agent",
    ],
  },
  "/qelbree": {
    topic_name: "qelbree",
    bg_image: "/qelbree-bg.png",
    brand_logo: "/qelbree.png",
    brand_name: "Qelbree",
    toggle_bg_color: "#202020",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#202020",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#822453",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#2F781E",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about Qelbree",
      "Savings and Support",
      "Find a Doctor",
      "Talk to a Live Agent",
    ],
  },
  "/diabetrix": {
    topic_name: "diabetrix",
    bg_image: "/diabetrix.png",
    brand_logo: "/diabetrix_logo.png",
    brand_name: "Diabetrix",
    toggle_bg_color: "#2C3E50",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#2C3E50",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#00868B",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#0077CC",
    brand_tertiary_text_color: "#FFFFFF",
    sms_phone_number: "+18629724788",
    sms_message:
      "Hi, I'm interested in learning more about Diabetrix. Can you help me?",
  },
  "/leqvio": {
    topic_name: "leqvio",
    bg_image: "/leqvio-bg.png",
    brand_logo: "/leqvio.png",
    brand_name: "LEQVIO",
    toggle_bg_color: "#712770",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#712770",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#007A94",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#2C6145",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about LEQVIO",
      "Get Cost Support",
      "Starting LEQVIO",
      "Talk to a Live Concierge",
    ],
  },
  "/xhance": {
    topic_name: "xhance",
    bg_image: "/xhance-bg.png",
    brand_logo: "/xhance.png",
    brand_name: "XHANCE",
    toggle_bg_color: "#712770",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#C66B3D",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#330072",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#662660",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about XHANCE",
      "Find a Doctor",
      "How to get XHANCE and Save",
      "Talk to a Live Agent",
      "Find a Pharmacy",
    ],
  },
  "/voquezna": {
    topic_name: "voquezna",
    bg_image: "/voquezna-bg.png",
    brand_logo: "/voquezna.png",
    brand_name: "VOQUEZNA",
    toggle_bg_color: "#0A61AE",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#0A61AE",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#F0507C",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#0CAA4F",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about VOQUEZNA",
      "Find a Doctor",
      "Savings Support",
      "Connect with a Live Agent",
    ],
  },
  "/actemra": {
    topic_name: "actemra",
    bg_image: "/actemra_bg.png",
    brand_logo: "/actemra.png",
    brand_name: "ACTEMRA",
    toggle_bg_color: "#8dc549",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#8D278B",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#1B9EC6",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#8dc549",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about ACTEMRA",
      "Get Patient Cost and Coverage Support",
      "Order ACTEMRA Materials",
      "Talk to a Live Concierge",
    ],
  },
  "/adbry": {
    topic_name: "adbry",
    bg_image: "/adbry_bg.png",
    brand_logo: "/actemra.png",
    brand_name: "Adbry",
    toggle_bg_color: "#C012A2",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#26326C",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#910E7B",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#00609C",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about Adbry",
      "Find a Doctor",
      "Savings and Support Programs",
      "Coverage Finder",
      "Talk to a Live Concierge",
    ],
  },
  "/adbry-hcp": {
    topic_name: "adbry-hcp",
    bg_image: "/adbry-hcp_bg.png",
    brand_logo: "/actemra.png",
    brand_name: "Adbry HCP",
    toggle_bg_color: "#BF17A1",
    toggle_text_color: "#FFFFFF",
    brand_primary_bg_color: "#BF17A1",
    brand_primary_text_color: "#FFFFFF",
    brand_secondary_bg_color: "#26326C",
    brand_secondary_text_color: "#FFFFFF",
    brand_tertiary_bg_color: "#00609C",
    brand_tertiary_text_color: "#FFFFFF",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about Adbry",
      "Support and Resources for Your Patients",
      "Request a Rep Visit",
      "Talk to a Live Concierge",
    ],
  },
  "/anzupgo": {
    topic_name: "anzupgo",
    bg_image: "/anzupgo_bg.png",
    brand_logo: "/anzupgo.png",
    brand_name: "Anzupgo",
    toggle_bg_color: "#88ED06",
    toggle_text_color: "#21292F",
    brand_primary_bg_color: "#80EB07",
    brand_primary_text_color: "#21292F",
    brand_secondary_bg_color: "#CCFF00",
    brand_secondary_text_color: "#21292F",
    brand_tertiary_bg_color: "#DBDEDF",
    brand_tertiary_text_color: "#21292F",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about Anzupgo and CHE",
      "Find a Doctor",
      "Savings and Support Programs",
      "Take the CHE Assessment",
      "Talk to a Live Concierge",
    ],
  },
  "/anzupgo-hcp": {
    topic_name: "anzupgo-hcp",
    bg_image: "/anzupgo-hcp_bg.png",
    brand_logo: "/anzupgo-hcp.png",
    brand_name: "Anzupgo HCP",
    toggle_bg_color: "#80EB07",
    toggle_text_color: "#21292F",
    brand_primary_bg_color: "#88ED06",
    brand_primary_text_color: "#21292F",
    brand_secondary_bg_color: "#CCFF00",
    brand_secondary_text_color: "#21292F",
    brand_tertiary_bg_color: "#DBDEDF",
    brand_tertiary_text_color: "#21292F",
    show_provider_modal: false,
    pop_up_list: [
      "Learn More about Anzupgo and CHE",
      "Support and Resources for Your Patients",
      "Request a Rep Visit",
      "Talk to a Live Concierge",
    ],
  },
};
